/**
 * Bulk Google Ads flow CSV upload (admin).
 *
 * Accepts a CSV, matches columns by header name, validates each row, previews
 * the result, then in one array-upsert switches every valid location onto the
 * direct Google Ads flow (`conversion_delivery_method = 'google_ads'`) and
 * writes its customer ID + conversion-action IDs.
 *
 * Header → field (case / space / underscore-insensitive):
 *   location_id            → location_id (required)
 *   customer_id            → google_ads_customer_id (required, digits only)
 *   survey_submissions_id  → google_ads_conversion_action_form_submit
 *   ghl_phone_calls_id     → google_ads_conversion_action_phone_call
 *   qualified_leads_id     → google_ads_conversion_action_qualified
 *   converted_leads_id     → google_ads_conversion_action_converted
 *   phone_call_only        → phone_call_only (boolean: true/1/yes)
 *
 * A row is accepted when it has location_id + customer_id + ≥1 conversion ID.
 * A phone_call_only row additionally requires BOTH the GHL phone-call ID and the
 * converted ID (its converted cycle uploads both on a confirmed booking).
 * Extra harvest columns (account_name, customer_id_dashed, notes, …) are ignored.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useBulkUpsertQualifierGoogleAdsSettings,
  useQualifierGoogleAdsFlowMap,
} from '@/hooks/useGHLQualifierDashboard'

type FieldKey =
  | 'location_id'
  | 'customer_id'
  | 'form_submit'
  | 'phone_call'
  | 'qualified'
  | 'converted'
  | 'phone_call_only'

const HEADER_ALIASES: Record<string, FieldKey> = {
  location_id: 'location_id',
  customer_id: 'customer_id',
  google_ads_customer_id: 'customer_id',
  survey_submissions_id: 'form_submit',
  service_submissions_id: 'form_submit',
  ghl_phone_calls_id: 'phone_call',
  qualified_leads_id: 'qualified',
  converted_leads_id: 'converted',
  phone_call_only: 'phone_call_only',
}

const TRUTHY = new Set(['true', '1', 'yes', 'y'])
const NAME_ALIASES = new Set(['account_name', 'name', 'business_name'])

/** Field → human label, for the read-only mapping summary. */
const FIELD_LABELS: { key: FieldKey; label: string; required: boolean }[] = [
  { key: 'location_id', label: 'Location ID', required: true },
  { key: 'customer_id', label: 'Customer ID', required: true },
  { key: 'qualified', label: 'Qualified Leads', required: false },
  { key: 'converted', label: 'Converted Leads', required: false },
  { key: 'form_submit', label: 'Survey Submissions', required: false },
  { key: 'phone_call', label: 'GHL Phone Calls', required: false },
  { key: 'phone_call_only', label: 'Phone Calls Only', required: false },
]

const normalizeHeader = (h: string): string =>
  h.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

interface ParsedRow {
  readonly rowNumber: number
  readonly name: string
  readonly locationId: string
  readonly customerId: string
  readonly formSubmit: string | null
  readonly phoneCall: string | null
  readonly qualified: string | null
  readonly converted: string | null
  readonly phoneCallOnly: boolean
  readonly convCount: number
  readonly valid: boolean
  readonly reason: string | null
}

interface Props {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function BulkGoogleAdsUploadDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast()
  const flowMap = useQualifierGoogleAdsFlowMap(open)
  const bulkMutation = useBulkUpsertQualifierGoogleAdsSettings()
  const inputRef = useRef<HTMLInputElement>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [fieldToHeader, setFieldToHeader] = useState<Partial<Record<FieldKey, string>>>({})
  const [isDragging, setIsDragging] = useState(false)
  const [testMode, setTestMode] = useState(false)

  const reset = useCallback(() => {
    setFileName(null)
    setParseError(null)
    setRows([])
    setFieldToHeader({})
    setIsDragging(false)
    setTestMode(false)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset()
      onOpenChange(next)
    },
    [onOpenChange, reset]
  )

  const parseText = useCallback((text: string) => {
    const result = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
    })
    if (result.errors && result.errors.length > 0) {
      setParseError(result.errors[0]?.message ?? 'Could not parse the CSV.')
      setRows([])
      setFieldToHeader({})
      return
    }

    const headers = result.meta.fields ?? []
    const f2h: Partial<Record<FieldKey, string>> = {}
    let nameHeader: string | undefined
    for (const h of headers) {
      const norm = normalizeHeader(h)
      const field = HEADER_ALIASES[norm]
      if (field && !f2h[field]) f2h[field] = h
      if (!nameHeader && NAME_ALIASES.has(norm)) nameHeader = h
    }

    const parsed: ParsedRow[] = result.data.map((obj, i) => {
      const get = (field: FieldKey): string => {
        const hdr = f2h[field]
        return hdr ? String(obj[hdr] ?? '').trim() : ''
      }
      const name = nameHeader ? String(obj[nameHeader] ?? '').trim() : ''
      const locationId = get('location_id')
      const customerId = get('customer_id').replace(/\D/g, '')
      const formSubmit = get('form_submit') || null
      const phoneCall = get('phone_call') || null
      const qualified = get('qualified') || null
      const converted = get('converted') || null
      const phoneCallOnly = TRUTHY.has(get('phone_call_only').toLowerCase())
      const convCount = [formSubmit, phoneCall, qualified, converted].filter(Boolean).length

      let reason: string | null = null
      if (!locationId) reason = 'missing location_id'
      else if (!customerId) reason = 'missing customer_id'
      // phone_call_only locations upload phone_call ~1h after contact creation
      // AND upload converted when the converted 8-day cycle confirms a booking
      // for a phone-call contact — so both IDs are required.
      else if (phoneCallOnly && (!phoneCall || !converted))
        reason = 'phone_call_only requires both the GHL phone call ID and the converted ID'
      else if (convCount === 0) reason = 'no conversion IDs'

      return {
        rowNumber: i + 2, // +1 for header row, +1 for 1-based
        name,
        locationId,
        customerId,
        formSubmit,
        phoneCall,
        qualified,
        converted,
        phoneCallOnly,
        convCount,
        valid: reason === null,
        reason,
      }
    })

    setFieldToHeader(f2h)
    setRows(parsed)
    setParseError(null)
  }, [])

  const onFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0]
      if (!file) return
      setFileName(file.name)
      const reader = new FileReader()
      reader.onload = () => parseText(String(reader.result ?? ''))
      reader.onerror = () => setParseError('Could not read the file.')
      reader.readAsText(file)
    },
    [parseText]
  )

  const requiredMissing = rows.length > 0 && (!fieldToHeader.location_id || !fieldToHeader.customer_id)

  const { validRows, invalidCount, alreadyOnFlow } = useMemo(() => {
    const valid = rows.filter((r) => r.valid)
    const onFlow = flowMap.data
      ? valid.filter((r) => flowMap.data!.has(r.locationId)).length
      : 0
    return { validRows: valid, invalidCount: rows.length - valid.length, alreadyOnFlow: onFlow }
  }, [rows, flowMap.data])

  const handleImport = useCallback(async () => {
    if (validRows.length === 0 || requiredMissing) return
    try {
      const { count } = await bulkMutation.mutateAsync({
        rows: validRows.map((r) => ({
          location_id: r.locationId,
          google_ads_customer_id: r.customerId,
          google_ads_conversion_action_qualified: r.qualified,
          google_ads_conversion_action_converted: r.converted,
          google_ads_conversion_action_form_submit: r.formSubmit,
          google_ads_conversion_action_phone_call: r.phoneCall,
          phone_call_only: r.phoneCallOnly,
        })),
        testMode,
      })
      toast({
        title: 'Import complete',
        description:
          `${count} location(s) set to the Google Ads flow${testMode ? ' (test mode — uploads suppressed)' : ''}.` +
          (invalidCount > 0 ? ` ${invalidCount} row(s) skipped.` : ''),
      })
      handleOpenChange(false)
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }, [validRows, requiredMissing, bulkMutation, testMode, invalidCount, toast, handleOpenChange])

  const hasFile = fileName !== null && parseError === null && rows.length >= 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Bulk-enable Google Ads flow</DialogTitle>
          <DialogDescription>
            Upload a CSV to switch locations onto the direct Google Ads flow and write their
            customer ID + conversion-action IDs in one go.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        {!hasFile || parseError ? (
          <>
            <label
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragging(false)
                onFiles(e.dataTransfer.files)
              }}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Drop a CSV here or click to choose</p>
              <p className="max-w-md text-xs text-muted-foreground">
                Expected columns: <code>location_id</code>, <code>customer_id</code>,{' '}
                <code>survey_submissions_id</code>, <code>ghl_phone_calls_id</code>,{' '}
                <code>qualified_leads_id</code>, <code>converted_leads_id</code>
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  onFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </label>
            {parseError ? (
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {parseError}
              </p>
            ) : null}
          </>
        ) : (
          <div className="space-y-4">
            {/* File + change */}
            <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="truncate text-sm font-medium">{fileName}</span>
                <span className="text-xs text-muted-foreground">({rows.length} rows)</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={() => inputRef.current?.click()}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Choose different file
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  onFiles(e.target.files)
                  e.target.value = ''
                }}
              />
            </div>

            {/* Detected mapping (read-only) */}
            <div className="rounded-md border p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Detected column mapping
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {FIELD_LABELS.map(({ key, label, required }) => {
                  const header = fieldToHeader[key]
                  return (
                    <div key={key} className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">
                        {label}
                        {required ? <span className="text-destructive"> *</span> : null}
                      </span>
                      {header ? (
                        <code className="truncate text-xs">{header}</code>
                      ) : required ? (
                        <span className="text-xs font-medium text-destructive">missing</span>
                      ) : (
                        <span className="text-xs text-muted-foreground/60">not in file</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {requiredMissing ? (
              <p className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                Required column missing — the CSV must have a <code>location_id</code> and a{' '}
                <code>customer_id</code> column.
              </p>
            ) : null}

            {/* Summary */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center gap-1.5 font-medium text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                {validRows.length} valid
              </span>
              {invalidCount > 0 ? (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  {invalidCount} skipped
                </span>
              ) : null}
              {alreadyOnFlow > 0 ? (
                <span className="text-muted-foreground">{alreadyOnFlow} already on flow (will update)</span>
              ) : null}
            </div>

            {/* Preview */}
            <ScrollArea className="h-[280px] rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Customer ID</TableHead>
                    <TableHead className="text-center">Conv.</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.rowNumber} className={r.valid ? '' : 'opacity-60'}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="truncate text-sm font-medium">
                            {r.name || r.locationId || <span className="text-destructive">—</span>}
                            {r.phoneCallOnly ? (
                              <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                                phone-only
                              </span>
                            ) : null}
                          </span>
                          {r.name && r.locationId ? (
                            <span className="font-mono text-xs text-muted-foreground">
                              {r.locationId}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.customerId || <span className="text-destructive">—</span>}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-xs">
                        {r.convCount}/4
                      </TableCell>
                      <TableCell>
                        {r.valid ? (
                          <span className="flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            ready
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {r.reason}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Test mode */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="bulk-ga-test-mode"
                checked={testMode}
                onCheckedChange={(v) => setTestMode(v === true)}
              />
              <Label htmlFor="bulk-ga-test-mode" className="text-sm font-normal leading-snug">
                Import in test mode
                <span className="block text-xs text-muted-foreground">
                  Sets <code>test_mode_enabled</code> — tags still apply but the cron won't upload live
                  conversions until you turn test mode off per location.
                </span>
              </Label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={bulkMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={validRows.length === 0 || requiredMissing || bulkMutation.isPending}
          >
            {bulkMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing…
              </>
            ) : (
              `Import ${validRows.length} location${validRows.length === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
